/*
 * notificationsCenterApi — the redesigned Notification Center + Preferences.
 * Standalone module on the shared apiClient so the frozen services/api.ts is
 * untouched (the established notificationPrefsApi pattern). Talks to
 * /api/notifications.
 */
import { api } from './apiClient';

export interface NotificationItem {
  id: number;
  type: string;
  title: string;
  body: string | null;
  link: string | null;
  is_read: number;
  category: string | null;
  priority: string;          // 'high' | 'normal'
  source_label: string | null;
  snoozed_until: string | null;
  created_at: string;
}

export interface NotificationSummary {
  total_unread: number;
  per_category: Record<string, number>;
}

export type CategoryTone = 'indigo' | 'paprika';
export interface NotifCategoryMeta {
  id: string;
  label: string;
  blurb: string;
  tone: CategoryTone;
  icon: string;
}

export interface EffectiveEvent {
  key: string;
  event: string;
  label: string;
  roleDefault: boolean;
  override: boolean | null;
  effective: boolean;
  overridden: boolean;
}
export interface EffectiveCategory {
  id: string;
  label: string;
  blurb: string;
  tone: CategoryTone;
  events: EffectiveEvent[];
}
export interface EffectivePrefs {
  role: string | null;
  categories: EffectiveCategory[];
  overrideCount: number;
}

export const notificationsCenterApi = {
  // ── Center ──
  list: (opts?: { unreadOnly?: boolean; category?: string; limit?: number; offset?: number }): Promise<NotificationItem[]> => {
    const p = new URLSearchParams();
    if (opts?.unreadOnly) p.set('unread_only', 'true');
    if (opts?.category && opts.category !== 'all') p.set('category', opts.category);
    if (opts?.limit != null) p.set('limit', String(opts.limit));
    if (opts?.offset != null) p.set('offset', String(opts.offset));
    const qs = p.toString();
    return api.get(`/notifications${qs ? `?${qs}` : ''}`).then((r) => r.data.data);
  },
  summary: (): Promise<NotificationSummary> =>
    api.get('/notifications/summary').then((r) => r.data.data),
  categories: (): Promise<NotifCategoryMeta[]> =>
    api.get('/notifications/categories').then((r) => r.data.data),

  markRead: (id: number): Promise<void> =>
    api.patch(`/notifications/${id}/read`).then(() => undefined),
  markUnread: (id: number): Promise<void> =>
    api.patch(`/notifications/${id}/unread`).then(() => undefined),
  snooze: (id: number, until?: string): Promise<{ snoozed_until: string }> =>
    api.post(`/notifications/${id}/snooze`, until ? { until } : {}).then((r) => r.data.data),
  markAllRead: (category?: string): Promise<void> =>
    api.post('/notifications/mark-all-read', category ? { category } : {}).then(() => undefined),

  // ── Preferences (role-default → user-override) ──
  getPreferences: (): Promise<EffectivePrefs> =>
    api.get('/notifications/preferences').then((r) => r.data.data),
  setPreference: (eventKey: string, value: boolean | null): Promise<EffectivePrefs> =>
    api.patch('/notifications/preferences', { event_key: eventKey, value }).then((r) => r.data.data),
  resetAll: (): Promise<EffectivePrefs> =>
    api.post('/notifications/preferences/reset-all', {}).then((r) => r.data.data),
  setCategoryEnabled: (category: string, enabled: boolean): Promise<EffectivePrefs> =>
    api.post('/notifications/preferences/category', { category, enabled }).then((r) => r.data.data),
};
